import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Music, Users, Headphones, Sparkles, Star, ChevronLeft, ChevronRight, Play, Crown, Calendar, Clock, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import apiClient, { redirectToAuth } from '../utils/api';
import { isTopArtist } from '../components/VerificationBadges';
import { motion } from 'framer-motion';

// Neon text component with tracing animation
const NeonTitle = ({ text }) => {
  return (
    <div className="relative inline-block">
      <motion.h1 
        className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight relative"
        style={{
          background: 'linear-gradient(135deg, #c084fc 0%, #a855f7 25%, #8b5cf6 50%, #7c3aed 75%, #c084fc 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {text}
      </motion.h1>
      
      <motion.h1 
        className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, #c084fc 0%, #a855f7 25%, #8b5cf6 50%, #7c3aed 75%, #c084fc 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          filter: 'blur(8px)',
          opacity: 0.6,
        }}
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        {text}
      </motion.h1>

      <svg 
        className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
        style={{ transform: 'scale(1.1)', transformOrigin: 'center' }}
      >
        <defs>
          <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="traceGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="40%" stopColor="transparent" />
            <stop offset="50%" stopColor="#c084fc" />
            <stop offset="60%" stopColor="transparent" />
            <stop offset="100%" stopColor="transparent" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="-1 0"
              to="1 0"
              dur="2.5s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        <rect x="0" y="5%" width="100%" height="90%" rx="8" ry="8" fill="none" stroke="url(#traceGradient)" strokeWidth="2" filter="url(#neonGlow)" style={{ opacity: 0.8 }} />
      </svg>

      <motion.div 
        className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 rounded-tl-md"
        style={{ borderColor: '#a855f7', boxShadow: '0 0 10px #a855f7' }}
        animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 rounded-tr-md"
        style={{ borderColor: '#8b5cf6', boxShadow: '0 0 10px #8b5cf6' }}
        animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      />
      <motion.div 
        className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 rounded-bl-md"
        style={{ borderColor: '#8b5cf6', boxShadow: '0 0 10px #8b5cf6' }}
        animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />
      <motion.div 
        className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 rounded-br-md"
        style={{ borderColor: '#a855f7', boxShadow: '0 0 10px #a855f7' }}
        animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
      />
    </div>
  );
};

// Social icon components
const SoundCloudIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.057-.05-.1-.1-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c.014.055.044.094.104.094s.09-.039.108-.094l.21-1.308-.21-1.334c-.018-.057-.047-.094-.108-.094m1.83-1.229c-.063 0-.112.05-.121.116l-.218 2.524.218 2.445c.009.066.058.116.121.116s.111-.05.12-.116l.248-2.445-.248-2.524c-.009-.066-.057-.116-.12-.116m4.668 1.139c-.158 0-.285.127-.285.285v6.163c0 .158.127.285.285.285h4.5c2.486 0 4.5-2.014 4.5-4.5s-2.014-4.233-4.5-4.233c-.772 0-1.5.197-2.133.544-.475-2.807-2.935-4.944-5.867-4.944-1.035 0-2.001.271-2.843.739-.252.141-.319.264-.322.524v8.795c.004.253.197.461.45.478h6.5"/></svg>
);
const InstagramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
);
const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.78a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.21z"/></svg>
);
const YouTubeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);

// Featured artist card with social links
const FeaturedArtistCard = ({ artist, onClick, idx }) => {
  const hasSocial = artist.soundcloud_url || artist.instagram_url || artist.tiktok_url || artist.youtube_url;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: idx * 0.1 }}
      className="flex-shrink-0 w-[320px] md:w-[360px]"
    >
      <Card
        data-testid={`featured-artist-${artist.artist_id}`}
        className="glass-default border-white/10 hover:border-violet-500/30 cursor-pointer overflow-hidden group transition-all duration-300"
        onClick={onClick}
      >
        <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20">
          {artist.media_urls && artist.media_urls[0] ? (
            <img
              src={artist.media_urls[0]}
              alt="Artist"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-16 h-16 text-violet-400/50" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-sm text-white/80 line-clamp-2">{artist.bio}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {artist.genres?.slice(0, 3).map((genre, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/30 text-violet-200 font-medium">
                  {genre}
                </span>
              ))}
            </div>
          </div>
          {artist.rating > 0 && (
            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              <span className="text-xs font-semibold">{(artist.rating || 0).toFixed(1)}</span>
            </div>
          )}
        </div>
        
        <CardContent className="p-4">
          {/* Social Links Row */}
          {hasSocial && (
            <div className="flex items-center gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
              {artist.soundcloud_url && (
                <a href={artist.soundcloud_url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-orange-500/20 hover:bg-orange-500/40 text-orange-300 transition-colors"
                  data-testid={`featured-social-sc-${artist.artist_id}`}>
                  <SoundCloudIcon />
                </a>
              )}
              {artist.instagram_url && (
                <a href={artist.instagram_url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-pink-500/20 hover:bg-pink-500/40 text-pink-300 transition-colors">
                  <InstagramIcon />
                </a>
              )}
              {artist.tiktok_url && (
                <a href={artist.tiktok_url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300 transition-colors">
                  <TikTokIcon />
                </a>
              )}
              {artist.youtube_url && (
                <a href={artist.youtube_url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-300 transition-colors">
                  <YouTubeIcon />
                </a>
              )}
            </div>
          )}
          
          {/* SoundCloud mini embed */}
          {artist.soundcloud_url && (
            <div className="rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <iframe
                title={`SoundCloud - ${artist.artist_id}`}
                width="100%"
                height="80"
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(artist.soundcloud_url)}&color=%238b5cf6&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false`}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [featuredDJ, setFeaturedDJ] = useState(null);
  const [spotlightIdx, setSpotlightIdx] = useState(0);
  const [topArtists, setTopArtists] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const carouselRef = useRef(null);

  // Site content with sensible fallbacks
  const heroTitle = siteContent.hero_title || 'RazientSounds';
  const heroTagline = siteContent.hero_tagline || 'Discover artists. Explore their sound. Step into the virtual nightclub.';
  const nightclubCtaTitle = siteContent.nightclub_cta_title || 'Step Into The Virtual Nightclub';
  const nightclubCtaText = siteContent.nightclub_cta_text || 'Walk around, meet other people, and vibe together in a multiplayer space. No account needed to explore.';
  const artistCtaTitle = siteContent.artist_cta_title || 'Are you an artist?';
  const artistCtaText = siteContent.artist_cta_text || 'Create your profile, link your SoundCloud, and get discovered by our community.';
  const heroBackgroundUrl = siteContent.hero_background_url || '';
  const artistCtaBackgroundUrl = siteContent.artist_cta_background_url || '';

  useEffect(() => {
    apiClient.get('/site-content').then(r => {
      if (r.data && typeof r.data === 'object') setSiteContent(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        // Fetch featured DJ
        const featuredDJRes = await apiClient.get('/featured-dj').catch(() => ({ data: { featured: null } }));
        if (featuredDJRes.data.featured) {
          setFeaturedDJ(featuredDJRes.data.featured);
        }
        
        // Fetch upcoming events
        apiClient.get('/events/upcoming').then(r => {
          if (Array.isArray(r.data)) setUpcomingEvents(r.data);
        }).catch(() => {});
        
        const featuredResponse = await apiClient.get('/featured-artists');
        if (featuredResponse.data && featuredResponse.data.length > 0) {
          setFeaturedArtists(featuredResponse.data);
        } else {
          const artistsResponse = await apiClient.get('/artists?status=approved');
          const sorted = artistsResponse.data
            .filter(a => a.status === 'approved')
            .sort((a, b) => (b.rating || 0) - (a.rating || 0))
            .slice(0, 8);
          setFeaturedArtists(sorted);
        }
      } catch (error) {
        try {
          const artistsResponse = await apiClient.get('/artists');
          setFeaturedArtists(artistsResponse.data.sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 8));
        } catch (e) { /* silent */ }
      }
    };
    fetchArtists();
  }, []);

  const topArtistsCount = topArtists.length;

  // Rotate the spotlight every 6 seconds
  useEffect(() => {
    if (topArtistsCount < 2) return;
    const t = setInterval(() => setSpotlightIdx(i => (i + 1) % topArtistsCount), 6000);
    return () => clearInterval(t);
  }, [topArtistsCount]);

  // Fetch Top Artists for the rotating spotlight (independent of curated featured list)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/artists');
        setTopArtists((res.data || []).filter(isTopArtist).slice(0, 5));
      } catch { /* silent */ }
    })();
  }, []);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 380;      carouselRef.current.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Optional admin-uploaded hero background */}
      {heroBackgroundUrl && (
        <div
          className="absolute inset-x-0 top-0 h-[80vh] z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${heroBackgroundUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            opacity: 0.55,
          }}
          aria-hidden="true"
          data-testid="hero-bg-image"
        />
      )}
      <div className="container mx-auto px-4 py-20 relative z-10">
        {/* Hero */}
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <NeonTitle text={heroTitle} />
            <motion.p 
              className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto whitespace-pre-line"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {heroTagline}
            </motion.p>
          </div>

          {/* Artists Tab - Glowing CTA */}
          <motion.div
            className="flex justify-center pt-4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div className="relative" whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <div className="absolute -inset-[1px] rounded-2xl blur-sm" style={{ background: 'linear-gradient(135deg, #c084fc, #a78bfa, #818cf8, #6366f1)', opacity: 0.5 }} />
              <motion.div
                className="absolute -inset-[1.5px] rounded-2xl"
                style={{ background: 'linear-gradient(90deg, #e879f9, #c084fc, #a78bfa, #818cf8, #a78bfa, #c084fc, #e879f9)', backgroundSize: '200% 100%' }}
                animate={{ backgroundPosition: ['0% 50%', '200% 50%'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              />
              <button
                data-testid="hero-browse-artists-btn"
                onClick={() => navigate('/browse')}
                className="relative flex items-center gap-3 px-8 py-3.5 rounded-2xl font-semibold text-sm tracking-wide bg-black/90 backdrop-blur-sm transition-all"
                style={{ boxShadow: '0 0 25px rgba(168, 85, 247, 0.35), 0 0 50px rgba(139, 92, 246, 0.15), inset 0 0 30px rgba(168, 85, 247, 0.08)' }}
              >
                <Music className="w-5 h-5" style={{ filter: 'drop-shadow(0 0 6px rgba(192, 132, 252, 0.9))' }} />
                <span style={{ textShadow: '0 0 12px rgba(192, 132, 252, 0.7)' }}>Browse Artists</span>
                <Star className="w-4 h-4 text-violet-300/80" style={{ filter: 'drop-shadow(0 0 4px rgba(192, 132, 252, 0.8))' }} />
              </button>
            </motion.div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {/* Enter Nightclub CTA - Primary */}
            <motion.div className="relative" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <div className="absolute inset-0 rounded-full blur-md" style={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)', opacity: 0.6 }} />
              <motion.div
                className="absolute -inset-[2px] rounded-full"
                style={{ background: 'linear-gradient(90deg, #c084fc, #a855f7, #8b5cf6, #6366f1, #8b5cf6, #a855f7, #c084fc)', backgroundSize: '200% 100%' }}
                animate={{ backgroundPosition: ['0% 50%', '200% 50%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              />
              <Button
                data-testid="enter-nightclub-btn"
                onClick={() => navigate('/nightclub')}
                size="lg"
                className="relative h-14 px-10 rounded-full font-bold uppercase text-sm bg-black/80 hover:bg-black/60 border-0 transition-all"
                style={{ boxShadow: '0 0 30px rgba(168, 85, 247, 0.5), 0 0 60px rgba(139, 92, 246, 0.3), inset 0 0 20px rgba(168, 85, 247, 0.1)' }}
              >
                <Play className="mr-2" size={18} style={{ filter: 'drop-shadow(0 0 4px rgba(192, 132, 252, 0.8))' }} />
                <span style={{ textShadow: '0 0 10px rgba(192, 132, 252, 0.8)' }}>Enter Nightclub</span>
              </Button>
            </motion.div>

            <Button
              data-testid="signup-button"
              onClick={() => navigate('/signup')}
              size="lg"
              variant="outline"
              className="h-14 px-10 rounded-full font-bold uppercase text-sm border-white/20 hover:bg-white/10 transition-all flex items-center gap-2"
            >
              Create Account
            </Button>
          </motion.div>
          
          <div className="flex items-center justify-center gap-6 pt-2">
            <span 
              onClick={() => navigate('/login')}
              className="text-white/50 hover:text-white/80 text-sm cursor-pointer transition-colors"
            >
              Already have an account? Sign In
            </span>
            <span className="text-white/20">|</span>
            <span
              onClick={() => navigate('/browse')}
              className="text-violet-400/70 hover:text-violet-300 text-sm cursor-pointer transition-colors"
            >
              Browse Artists
            </span>
          </div>
        </div>

        {/* Features Grid - Updated */}
        <div className="max-w-5xl mx-auto mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Music, title: 'Artist Showcase', description: 'Discover profiles, listen to tracks, and follow your favorites' },
            { icon: Users, title: 'Virtual Nightclub', description: 'Walk around, meet people, and vibe in the multiplayer space' },
            { icon: Headphones, title: 'Live Sound', description: 'Stream music with embedded SoundCloud players on every profile' },
            { icon: Sparkles, title: 'Guest Access', description: 'Jump into the nightclub instantly — no account required' },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              className="glass-default rounded-2xl p-6 hover:border-violet-500/20 border border-transparent transition-all"
              data-testid={`feature-card-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <feature.icon className="w-10 h-10 text-violet-400 mb-4" />
              <h3 className="text-lg font-bold mb-1">{feature.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Featured DJ of the Week Spotlight */}
        {featuredDJ && (
          <motion.div 
            className="max-w-5xl mx-auto mt-28"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-2xl overflow-hidden" data-testid="featured-dj-spotlight">
              {/* Animated border */}
              <div className="absolute inset-0 rounded-2xl" style={{
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706, #f59e0b, #fbbf24)',
                backgroundSize: '200% 200%',
                animation: 'shimmerBorder 3s ease infinite',
                padding: '2px'
              }}>
                <div className="w-full h-full rounded-2xl bg-[#0d0d1a]" />
              </div>
              
              <div className="relative z-10 p-8 md:p-10">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Left - Info */}
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-yellow-500/20">
                        <Crown className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-widest text-yellow-400/80 font-semibold">DJ of the Week</p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white">
                          {featuredDJ.user_name || 'Featured Artist'}
                        </h2>
                      </div>
                    </div>
                    
                    {featuredDJ.featured_note && (
                      <p className="text-white/60 text-lg italic">"{featuredDJ.featured_note}"</p>
                    )}
                    
                    <p className="text-white/50">{featuredDJ.bio}</p>
                    
                    {featuredDJ.genres?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {featuredDJ.genres.map((genre, i) => (
                          <span key={i} className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500/20">
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Social links */}
                    <div className="flex items-center gap-2 pt-2">
                      {featuredDJ.soundcloud_url && (
                        <a href={featuredDJ.soundcloud_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 rounded-full bg-orange-500/20 hover:bg-orange-500/40 text-orange-300 transition-colors">
                          <SoundCloudIcon />
                        </a>
                      )}
                      {featuredDJ.instagram_url && (
                        <a href={featuredDJ.instagram_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 rounded-full bg-pink-500/20 hover:bg-pink-500/40 text-pink-300 transition-colors">
                          <InstagramIcon />
                        </a>
                      )}
                      {featuredDJ.tiktok_url && (
                        <a href={featuredDJ.tiktok_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 rounded-full bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300 transition-colors">
                          <TikTokIcon />
                        </a>
                      )}
                      {featuredDJ.youtube_url && (
                        <a href={featuredDJ.youtube_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-300 transition-colors">
                          <YouTubeIcon />
                        </a>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => navigate(`/artist/${featuredDJ.artist_id}`)}
                      className="h-12 px-8 rounded-full font-bold bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] transition-all"
                      data-testid="featured-dj-view-btn"
                    >
                      View Full Profile
                    </Button>
                  </div>

                  {/* Right - SoundCloud Embed */}
                  <div className="w-full md:w-[360px] flex-shrink-0">
                    {featuredDJ.soundcloud_url ? (
                      <div className="rounded-xl overflow-hidden border border-white/10">
                        <iframe
                          title="Featured DJ - SoundCloud"
                          width="100%"
                          height="300"
                          scrolling="no"
                          frameBorder="no"
                          allow="autoplay"
                          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(featuredDJ.soundcloud_url)}&color=%23f59e0b&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`}
                        />
                      </div>
                    ) : (
                      <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center">
                        <Music className="w-12 h-12 text-yellow-400/50 mx-auto mb-3" />
                        <p className="text-white/40">No SoundCloud linked yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <style>{`
              @keyframes shimmerBorder {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
            `}</style>
          </motion.div>
        )}

        {/* Top Artist Spotlight — rotating */}
        {topArtists.length > 0 && (() => {
          const s = topArtists[spotlightIdx % topArtists.length];
          return (
            <div className="max-w-5xl mx-auto mt-24 px-2" data-testid="artist-spotlight">
              <motion.div
                key={s.artist_id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-r from-amber-500/10 via-violet-500/10 to-indigo-500/10 p-8 md:p-10"
              >
                <div className="absolute top-0 left-0 top-artist-ribbon rounded-br-2xl px-4 py-1.5">
                  <span className="text-xs font-extrabold text-amber-950 tracking-widest">🏆 TOP ARTIST SPOTLIGHT</span>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-8 mt-6">
                  <div className="w-28 h-28 rounded-full overflow-hidden bg-violet-500/20 flex items-center justify-center shrink-0 ring-4 ring-amber-400/40">
                    {s.media_urls?.[0] ? (
                      <img src={s.media_urls[0]} alt={s.stage_name || s.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Star className="w-10 h-10 text-amber-300" />
                    )}
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-3xl font-bold mb-1" data-testid="spotlight-artist-name">{s.stage_name || s.name || 'Artist'}</h3>
                    <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="text-amber-300 font-semibold">{s.rating}</span>
                      <span className="text-white/40 text-sm">({s.review_count} reviews)</span>
                    </div>
                    <p className="text-white/50 text-sm capitalize">{(s.genres || []).slice(0, 3).join(' · ')}</p>
                  </div>
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <Button
                      data-testid="spotlight-book-btn"
                      onClick={() => navigate(`/artist/${s.artist_id}`)}
                      className="h-12 px-8 rounded-full font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-amber-950 shadow-lg shadow-amber-500/30"
                    >
                      Book {s.stage_name || s.name || 'Now'}
                    </Button>
                  </div>
                </div>
                {topArtists.length > 1 && (
                  <div className="flex gap-1.5 justify-center mt-6">
                    {topArtists.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setSpotlightIdx(i)}
                        className={`h-1.5 rounded-full transition-all ${i === spotlightIdx % topArtists.length ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/20'}`}
                        aria-label={`Spotlight ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          );
        })()}

        {/* Featured Artists Carousel */}
        {featuredArtists.length > 0 && (
          <div className="max-w-6xl mx-auto mt-28">
            <div className="flex items-end justify-between mb-8">
              <div>
                <motion.h2 
                  className="text-3xl md:text-4xl font-bold"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <span className="gradient-text">Featured Artists</span>
                </motion.h2>
                <p className="text-white/50 mt-2">Listen, follow, and discover new sounds</p>
              </div>
              
              {featuredArtists.length > 3 && (
                <div className="hidden md:flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => scrollCarousel(-1)}
                    className="rounded-full border border-white/10 hover:border-violet-500/30 hover:bg-violet-500/10 w-10 h-10"
                    data-testid="carousel-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => scrollCarousel(1)}
                    className="rounded-full border border-white/10 hover:border-violet-500/30 hover:bg-violet-500/10 w-10 h-10"
                    data-testid="carousel-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>

            {/* Carousel */}
            <div 
              ref={carouselRef}
              className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              data-testid="featured-artists-carousel"
            >
              {featuredArtists.map((artist, idx) => (
                <FeaturedArtistCard
                  key={artist.artist_id}
                  artist={artist}
                  idx={idx}
                  onClick={() => navigate(`/artist/${artist.artist_id}`)}
                />
              ))}
            </div>
            
            <div className="text-center mt-8">
              <Button
                data-testid="view-all-artists-button"
                onClick={() => navigate('/browse')}
                size="lg"
                className="h-12 px-8 rounded-full font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all"
              >
                View All Artists
              </Button>
            </div>
          </div>
        )}

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div className="max-w-5xl mx-auto mt-28" data-testid="upcoming-events-section">
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-2"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="gradient-text">Upcoming Events</span>
            </motion.h2>
            <p className="text-white/50 mb-8">Don't miss what's coming next</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {upcomingEvents.slice(0, 6).map((event, idx) => {
                const eventDate = new Date(`${event.event_date}T${event.event_time || '20:00'}`);
                return (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    data-testid={`upcoming-event-${event.event_id}`}
                  >
                    <Card className="glass-default border-white/10 hover:border-cyan-500/30 transition-all overflow-hidden group">
                      <CardContent className="p-5">
                        <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{event.title}</h3>
                        {event.description && (
                          <p className="text-white/50 text-sm mb-3 line-clamp-2">{event.description}</p>
                        )}
                        <div className="space-y-1.5 text-sm">
                          <div className="flex items-center gap-2 text-white/60">
                            <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                            <span>{eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-2 text-white/60">
                            <Clock className="w-3.5 h-3.5 text-cyan-400" />
                            <span>{eventDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2 text-white/60">
                              <MapPin className="w-3.5 h-3.5 text-cyan-400" />
                              <span className="line-clamp-1">{event.location}</span>
                            </div>
                          )}
                        </div>
                        {event.countdown_days > 0 && (
                          <div className="mt-3 px-2 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-center">
                            <span className="text-cyan-300 text-xs font-semibold">
                              {event.countdown_days} day{event.countdown_days !== 1 ? 's' : ''} away
                            </span>
                          </div>
                        )}
                        <div className="mt-3 flex items-center justify-between text-xs text-white/40">
                          <span>{event.attendee_count || 0} attending</span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            
            <div className="text-center mt-8">
              <Button
                data-testid="view-all-events-button"
                onClick={() => navigate('/events')}
                size="lg"
                variant="outline"
                className="h-12 px-8 rounded-full font-semibold border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 transition-all"
              >
                View All Events
              </Button>
            </div>
          </div>
        )}

        {/* Nightclub CTA */}
        <motion.div 
          className="max-w-4xl mx-auto mt-28 relative rounded-3xl overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900/60 via-purple-900/40 to-fuchsia-900/60 backdrop-blur-sm" />
          <div className="absolute inset-0 border border-violet-500/20 rounded-3xl" />
          <div className="relative p-10 md:p-14 text-center space-y-6">
            <div className="flex justify-center mb-2">
              <div className="p-3 rounded-2xl bg-violet-500/20 border border-violet-500/30">
                <Users className="w-8 h-8 text-violet-300" />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold">
              {nightclubCtaTitle}
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto whitespace-pre-line">
              {nightclubCtaText}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              <Button
                data-testid="nightclub-cta-enter"
                onClick={() => navigate('/nightclub')}
                size="lg"
                className="h-14 px-10 rounded-full font-bold uppercase text-sm bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all"
              >
                Enter as Guest
              </Button>
              <Button
                data-testid="nightclub-cta-signup"
                onClick={() => navigate('/signup')}
                size="lg"
                variant="outline"
                className="h-14 px-10 rounded-full font-bold uppercase text-sm border-2 border-violet-500/50 text-violet-300 hover:bg-violet-500/20 transition-all"
              >
                Create Account to Chat
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Artist Signup CTA */}
        <div
          className="max-w-3xl mx-auto mt-28 glass-heavy rounded-3xl p-12 text-center space-y-6 relative overflow-hidden"
          style={artistCtaBackgroundUrl ? {
            backgroundImage: `linear-gradient(rgba(15, 5, 25, 0.85), rgba(15, 5, 25, 0.85)), url(${artistCtaBackgroundUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : {}}
          data-testid="artist-cta-section"
        >
          <h2 className="text-3xl md:text-4xl font-bold relative z-10">
            {artistCtaTitle}
          </h2>
          <p className="text-white/70 text-lg whitespace-pre-line relative z-10">
            {artistCtaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="artist-signup-button"
              onClick={() => navigate('/signup')}
              size="lg"
              className="h-14 px-10 rounded-full font-bold uppercase text-sm bg-gradient-to-r from-fuchsia-600 to-red-600 hover:from-fuchsia-500 hover:to-red-500 shadow-[0_0_20px_rgba(217,70,239,0.4)] hover:shadow-[0_0_30px_rgba(217,70,239,0.6)] transition-all"
            >
              Become an Artist
            </Button>
            <Button
              data-testid="browse-artists-button-bottom"
              onClick={() => navigate('/browse')}
              size="lg"
              variant="outline"
              className="h-14 px-10 rounded-full font-bold uppercase text-sm border-2 border-violet-500 text-violet-400 hover:bg-violet-500/20 transition-all"
            >
              Browse Artists
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
